import React, { useState, useEffect } from 'react';
import { Navbar, roleNavPermissions } from './components/Navbar';
import { DemoNoticeBanner } from './components/DemoNoticeBanner';
import { CentralDashboard } from './components/CentralDashboard';
import { Observability } from './components/Observability';
import { RiskReview } from './components/RiskReview';
import { SafetyProfiles } from './components/SafetyProfiles';
import { ReleaseValidation } from './components/ReleaseValidation';
import { RuntimeSimulation } from './components/RuntimeSimulation';
import { EnterpriseArch } from './components/EnterpriseArch';
import { ApiDocs } from './components/ApiDocs';
import { Integrations } from './components/Integrations';
import { Account } from './components/Account';
import { PolicyRegistry } from './components/PolicyRegistry';
import { HowItWorks } from './components/HowItWorks';
import { IntegrationLab } from './components/IntegrationLab';
import { PlatformStats } from './components/PlatformStats';
import { DocumentationHub } from './components/DocumentationHub';
import { AgentPlayground } from './components/AgentPlayground';
import { AuthSession, TenantWorkspace } from './types';
import { Changelog } from './components/Changelog';
import { Download } from './components/Download';
import { Home } from './components/Home';
import { VerifyClaims } from './components/VerifyClaims';
import { Method } from './components/Method';
import { Dataset } from './components/Dataset';
import { api } from './services/api';
import { AlertTriangle, Lock, RefreshCw, ShieldCheck } from 'lucide-react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

function SignInRequired({ onSignIn }: { onSignIn: () => void }) {
  return (
    <div className="mx-auto max-w-xl py-12 text-center">
      <div className="glass-panel rounded-2xl p-8">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-100 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300">
          <Lock className="h-6 w-6" />
        </div>
        <h2 className="mt-4 text-xl font-bold text-slate-900 dark:text-white">Organization sign-in required</h2>
        <p className="mt-2 text-xs leading-5 text-slate-600 dark:text-slate-400">
          Sign in to access your organization’s AI applications, security policies, and trace telemetry.
        </p>
        <button
          type="button"
          onClick={onSignIn}
          className="mt-6 inline-flex items-center justify-center rounded-xl bg-brand-600 px-5 py-2.5 text-xs font-bold text-white shadow-sm transition hover:bg-brand-500"
        >
          Sign in to workspace
        </button>
      </div>
    </div>
  );
}

function PublicHome({ onSignIn, setActiveTab }: { onSignIn: () => void; setActiveTab: (tab: string) => void }) {
  return (
    <div className="mx-auto max-w-4xl space-y-6 py-8">
      <div className="glass-panel rounded-3xl p-8 sm:p-12 text-center space-y-4">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-brand-100 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300">
          <ShieldCheck className="h-8 w-8" />
        </div>
        <h1 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
          Continuous AI Agent Safety Assurance
        </h1>
        <p className="max-w-2xl mx-auto text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
          Safety boundaries for AI agents: decided before the agent is built, approved by a person, and
          enforced deterministically at runtime.
        </p>
        <div className="pt-4 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => setActiveTab('dashboard')}
            className="rounded-xl bg-brand-600 px-6 py-3 text-xs font-bold text-white shadow-md transition hover:bg-brand-500"
          >
            Explore Live Dashboard
          </button>
          <button
            type="button"
            onClick={onSignIn}
            className="rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-6 py-3 text-xs font-bold text-slate-700 dark:text-slate-200 shadow-sm transition hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            Sign in to Workspace
          </button>
        </div>
      </div>
    </div>
  );
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Unhandled UI render error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white flex items-center justify-center p-6">
          <div className="glass-panel p-8 rounded-2xl max-w-lg w-full border border-rose-300 dark:border-rose-800 text-center space-y-4 shadow-lg">
            <div className="w-12 h-12 rounded-full bg-rose-500/20 text-rose-500 mx-auto flex items-center justify-center border border-rose-500/30">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <h2 className="text-xl font-bold">Something went wrong</h2>
            <p className="text-xs text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-900 p-3 rounded text-left overflow-x-auto">
              The platform hit an unexpected display error. Reload to try the page again.
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.reload();
              }}
              className="px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-500 text-white text-xs font-bold transition-all flex items-center space-x-2 mx-auto"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Reload Platform</span>
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export function App() {
  const routeToTab: Record<string, string> = {
    '/': 'home',
    '/verify': 'verify',
    '/method': 'method',
    '/dataset': 'dataset',
    '/collectsea': 'dataset',
    '/why': 'method',
    '/dashboard': 'dashboard',
    '/guide': 'guide',
    '/docs': 'docs',
    '/documentation': 'docs',
    '/playground': 'playground',
    '/integration-lab': 'lab',
    '/observability': 'observability',
    '/review': 'review',
    '/profile': 'profiles',
    '/profiles': 'profiles',
    '/validate': 'validate',
    '/simulate': 'simulate',
    '/enterprise': 'enterprise',
    '/api-docs': 'api',
    '/integrations': 'integrations',
    '/account': 'account',
    '/policies': 'policies',
    '/stats': 'stats',
    '/changelog': 'changelog',
    '/download': 'download'
  };
  const tabToRoute: Record<string, string> = {
    dashboard: '/dashboard',
    home: '/',
    verify: '/verify',
    method: '/method',
    dataset: '/dataset',
    guide: '/guide',
    docs: '/docs',
    playground: '/playground',
    lab: '/integration-lab',
    observability: '/observability',
    review: '/review',
    profiles: '/profile',
    validate: '/validate',
    simulate: '/simulate',
    enterprise: '/enterprise',
    api: '/api-docs',
    integrations: '/integrations',
    account: '/account',
    policies: '/policies',
    stats: '/stats',
    changelog: '/changelog',
    download: '/download'
  };
  const [activeTab, setActiveTabState] = useState<string>(() => {
    if (typeof window !== 'undefined' && window.location.hostname.startsWith('docs.')) {
      return 'docs';
    }
    return routeToTab[window.location.pathname] || 'home';
  });
  const [session, setSession] = useState<AuthSession | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [tenants, setTenants] = useState<TenantWorkspace[]>([]);
  const [activeTenant, setActiveTenant] = useState<TenantWorkspace | null>(null);
  const [isDark, setIsDark] = useState<boolean>(() => {
    const stored = localStorage.getItem('agenticscale_theme');
    return stored === 'dark';
  });

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('agenticscale_theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('agenticscale_theme', 'light');
    }
  }, [isDark]);

  useEffect(() => {
    const onPopState = () => setActiveTabState(routeToTab[window.location.pathname] || 'dashboard');
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    let active = true;
    Promise.all([api.getSession(), api.getTenants().catch(() => [])])
      .then(([nextSession, tenantsData]) => { 
        if (active) {
          if (tenantsData && tenantsData.length > 0) {
            setTenants(tenantsData);
            const savedTenantId = localStorage.getItem('agenticscale_active_tenant');
            const matched = tenantsData.find(t => t.id === savedTenantId) || tenantsData[0];
            setActiveTenant(matched);
            localStorage.setItem('agenticscale_active_tenant', matched.id);
            if (nextSession.organization) {
              nextSession.organization.id = matched.id;
              nextSession.organization.name = matched.name;
            }
          }
          if (nextSession.demo_mode && nextSession.organization) {
            nextSession.organization.role = 'viewer';
          }
          setSession(nextSession); 
        }
      })
      .catch(() => { 
        if (active) {
          setSession({ 
            authenticated: false, 
            demo_mode: true, 
            provider: 'demo', 
            login_url: '/api/auth/login', 
            user: { id: 'user-public-demo', email: 'demo@agenticscale.org', name: 'Public demo' },
            organization: { id: 'org-demo-agenticscale', name: 'AgenticScale Demo Organization', role: 'viewer' }
          }); 
        } 
      })
      .finally(() => { if (active) setSessionLoading(false); });
    return () => { active = false; };
  }, []);

  const handleSwitchTenant = (tenant: TenantWorkspace) => {
    setActiveTenant(tenant);
    localStorage.setItem('agenticscale_active_tenant', tenant.id);
    setSession((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        organization: {
          id: tenant.id,
          name: tenant.name,
          role: prev.organization?.role || 'admin'
        }
      };
    });
  };

  const handleSwitchRole = (newRole: 'owner' | 'admin' | 'operator' | 'viewer') => {
    setSession((prev) => {
      const emailMap: Record<string, string> = {
        admin: 'alex.admin@enterprise.ai',
        operator: 'sarah.dev@enterprise.ai',
        owner: 'elena.owner@enterprise.ai',
        viewer: 'auditor.lead@compliance-firm.com'
      };
      const nameMap: Record<string, string> = {
        admin: 'Alex Chen (AI Admin)',
        operator: 'Sarah Jenkins (Developer)',
        owner: 'Elena Vance (Owner)',
        viewer: 'SOC 2 Auditor (Viewer)'
      };

      if (!prev) {
        return {
          authenticated: false,
          demo_mode: true,
          provider: 'demo',
          login_url: '/api/auth/login',
          user: { id: `user-${newRole}`, email: emailMap[newRole], name: nameMap[newRole] },
          organization: { id: 'org-demo-agenticscale', name: 'org-demo-agenticscale', role: newRole }
        };
      }

      return {
        ...prev,
        user: prev.user ? { ...prev.user, email: emailMap[newRole] || prev.user.email, name: nameMap[newRole] || prev.user.name } : { id: `user-${newRole}`, email: emailMap[newRole], name: nameMap[newRole] },
        organization: prev.organization 
          ? { ...prev.organization, role: newRole } 
          : { id: 'org-demo-agenticscale', name: 'org-demo-agenticscale', role: newRole }
      };
    });

    const targetPerms = roleNavPermissions[newRole];
    if (targetPerms) {
      const allowed = [
        'guide',
        'dashboard',
        ...targetPerms.allowedSafetyStages,
        ...targetPerms.allowedOperations,
        ...targetPerms.allowedResources
      ];
      if (!allowed.includes(activeTab)) {
        setActiveTab('dashboard');
      }
    }
  };

  const setActiveTab = (tab: string) => {
    const nextPath = tabToRoute[tab] || '/';
    if (window.location.pathname !== nextPath) window.history.pushState({}, '', nextPath);
    setActiveTabState(tab);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const signIn = () => {
    window.location.href = session?.login_url || '/api/auth/login?returnTo=%2Fdashboard';
  };

  const signOut = () => {
    window.location.href = '/api/auth/logout';
  };

  const hasWorkspaceAccess = Boolean(session?.authenticated || session?.demo_mode);
  const demoMode = Boolean(session?.demo_mode);
  const role = session?.organization?.role || 'admin';
  const canOperate = Boolean(hasWorkspaceAccess && ['owner', 'admin', 'operator'].includes(role));
  const canAdminister = Boolean(hasWorkspaceAccess && ['owner', 'admin'].includes(role));

  if (sessionLoading) {
    return <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center text-sm text-slate-500">Loading secure workspace…</div>;
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 flex flex-col justify-between selection:bg-brand-500 selection:text-white transition-colors duration-150">
        
        {/* Top Navbar with Persona & Tenant Switcher */}
        <Navbar 
          activeTab={activeTab} 
          setActiveTab={setActiveTab}
          isDark={isDark}
          setIsDark={setIsDark}
          session={session}
          onSignIn={signIn}
          onSignOut={signOut}
          onSwitchRole={handleSwitchRole}
          tenants={tenants}
          activeTenant={activeTenant}
          onSwitchTenant={handleSwitchTenant}
        />

        {/* Main Content View */}
        {/* Same width as the navigation row above, so the header never spans
            wider than the content under it. */}
        <main className="mx-auto w-full max-w-7xl 2xl:max-w-shell flex-grow px-4 py-8 sm:px-6 lg:px-8">
          {/* The persona banner used to sit here, on every page, offering every
              visitor a choice between Admin, Operator, Owner and Viewer and a
              Sign In beside it. Nobody arriving at a safety tool for the first
              time knows what a persona is or why they have been made an Owner
              of something, and the sign-in led to a login wall for one
              organization. Role-based access is a real capability and worth
              showing — but as something a reader chooses to try, on the page
              that explains what the roles are, not as furniture on all
              twenty-two. It now lives under "Running it for a team".
              See src/components/DemoNoticeBanner.tsx. */}

          {activeTab === 'home' && <Home setActiveTab={setActiveTab} />}
          {activeTab === 'verify' && <VerifyClaims />}
          {activeTab === 'method' && <Method setActiveTab={setActiveTab} />}
          {activeTab === 'dataset' && <Dataset />}
          {activeTab === 'guide' && <HowItWorks setActiveTab={setActiveTab} authenticated={Boolean(session?.authenticated)} onSignIn={signIn} />}
          {activeTab === 'lab' && <IntegrationLab setActiveTab={setActiveTab} authenticated={Boolean(session?.authenticated)} onSignIn={signIn} />}
          {activeTab === 'dashboard' && (hasWorkspaceAccess ? <CentralDashboard setActiveTab={setActiveTab} readOnly={!canOperate} demoMode={demoMode} role={role} /> : <PublicHome onSignIn={signIn} setActiveTab={setActiveTab} />)}
          {activeTab === 'observability' && (hasWorkspaceAccess ? <Observability key={`${session?.user?.id}:${session?.organization?.id}:${role}:${demoMode}`} setActiveTab={setActiveTab} demoMode={demoMode} /> : <SignInRequired onSignIn={signIn} />)}
          {activeTab === 'review' && (hasWorkspaceAccess ? <RiskReview setActiveTab={setActiveTab} readOnly={!canOperate} demoMode={demoMode} role={role} /> : <SignInRequired onSignIn={signIn} />)}
          {activeTab === 'profiles' && (hasWorkspaceAccess ? <SafetyProfiles setActiveTab={setActiveTab} readOnly={!canOperate} demoMode={demoMode} role={role} /> : <SignInRequired onSignIn={signIn} />)}
          {activeTab === 'validate' && (hasWorkspaceAccess ? <ReleaseValidation setActiveTab={setActiveTab} readOnly={!canOperate} demoMode={demoMode} role={role} /> : <SignInRequired onSignIn={signIn} />)}
          {activeTab === 'simulate' && (hasWorkspaceAccess ? <RuntimeSimulation setActiveTab={setActiveTab} demoMode={demoMode} /> : <SignInRequired onSignIn={signIn} />)}
          {activeTab === 'enterprise' && <EnterpriseArch session={session} onSignIn={signIn} onSwitchRole={handleSwitchRole} />}
          {activeTab === 'api' && <ApiDocs />}
          {activeTab === 'integrations' && (hasWorkspaceAccess ? <Integrations setActiveTab={setActiveTab} role={role} /> : <SignInRequired onSignIn={signIn} />)}
          {activeTab === 'account' && (hasWorkspaceAccess ? <Account session={session!} setActiveTab={setActiveTab} onSwitchRole={handleSwitchRole} tenants={tenants} activeTenant={activeTenant} onSwitchTenant={handleSwitchTenant} /> : <SignInRequired onSignIn={signIn} />)}
          {activeTab === 'docs' && <DocumentationHub setActiveTab={setActiveTab} authenticated={Boolean(session?.authenticated)} />}
          {activeTab === 'playground' && <AgentPlayground setActiveTab={setActiveTab} authenticated={Boolean(session?.authenticated)} onSignIn={signIn} />}
          {activeTab === 'policies' && (hasWorkspaceAccess ? <PolicyRegistry readOnly={!canAdminister} demoMode={demoMode} role={role} /> : <SignInRequired onSignIn={signIn} />)}
          {activeTab === 'stats' && <PlatformStats activeTenant={activeTenant} />}
          {activeTab === 'changelog' && <Changelog setActiveTab={setActiveTab} />}
          {activeTab === 'download' && <Download setActiveTab={setActiveTab} />}
        </main>

      </div>
    </ErrorBoundary>
  );
}

export default App;
